import { CalendarDays, Pencil, Plus } from 'lucide-react'

import { ActionInfoQuiet, Button, ErrorNotice, QueryStatePanel, ScreenInfo, StatusMessage } from '@krp/ui-kit'

import { SrOnly } from './parts'
import type { PlacesActions, PlacesVm } from './screenTypes'
import './schedule.css'

const SCREEN_INFO = [
  'Объект — место, где работают кассиры: магазин, точка, любое название, например «Волная 28». Объекты ни к чему не привязаны.',
  'Нажмите на плитку объекта, чтобы открыть его график: таблицу месяца, где слева сотрудники объекта, а сверху дни. Нажатие на клетку ставит кассира на день или снимает.',
  'Карандаш в правом верхнем углу плитки открывает форму объекта: переименовать его или убрать в архив.',
  'На плитке написано, сколько кассиров выходит на объекте сегодня.',
  'Раздел «По кассирам»: выберите кассира — увидите его месяц по всем объектам и сможете поставить или снять его там же. Каждому кассиру присвоен свой цвет.',
  'Создавать и менять объекты может тот, кому выдано право править график.',
] as const

/** Заглушка на время загрузки: плитки тех же размеров, чтобы экран не прыгал, когда придут данные. */
function PlacesSkeleton() {
  return (
    <div className="sch-places" role="status" aria-live="polite">
      <SrOnly>Загружаем объекты…</SrOnly>
      {Array.from({ length: 3 }, (_, index) => (
        <span className="sch-skeleton__tile" key={index} aria-hidden="true" />
      ))}
    </div>
  )
}

/**
 * «График выхода» (вход раздела): плитки объектов. Нажатие на плитку открывает график объекта; «Создать объект» и «Изменить»
 * показываются только тому, кому выдано право править. Экран ничего не запрашивает сам: vm и действия — от binding.
 */
export function PlacesScreen({ vm, actions }: { vm: PlacesVm; actions: PlacesActions }) {
  return (
    <section aria-labelledby="schedule-title" className="sch">
      <header className="sch-head">
        <div className="sch-head__title">
          <h2 id="schedule-title">График выхода</h2>
          <ScreenInfo title="График выхода" lines={SCREEN_INFO} />
        </div>
        <p className="sch-lead">Объекты и кто из кассиров на них выходит.</p>
      </header>

      <nav className="sch-switch" aria-label="Разделы графика выхода">
        <span className="sch-switch__current" aria-current="page">
          Объекты
        </span>
        <ActionInfoQuiet>
          <Button variant="secondary" actionId="schedule.home.show-cashiers" onClick={actions.openCashiers}>
            По кассирам
          </Button>
        </ActionInfoQuiet>
      </nav>

      {vm.canManage && (
        <div className="sch-actions">
          <ActionInfoQuiet>
            <Button variant="primary" icon={<Plus size={16} aria-hidden="true" />} actionId="schedule.home.create-place" onClick={actions.createPlace}>
              Создать объект
            </Button>
          </ActionInfoQuiet>
        </div>
      )}

      {vm.load.status === 'loading' || vm.load.status === 'idle' ? (
        <PlacesSkeleton />
      ) : vm.load.status !== 'ready' ? (
        <QueryStatePanel
          query={vm.load}
          emptyText=""
          onRetry={actions.reload}
          retryActionId="schedule.home.reload"
          titles={{ forbidden: 'Нет доступа к графику выхода', unavailable: 'Служба графика недоступна', error: 'Не удалось получить объекты' }}
        />
      ) : vm.places.length === 0 ? (
        <StatusMessage
          kind="empty"
          title="Объектов пока нет"
          text={vm.canManage ? 'Нажмите «Создать объект» и назовите магазин или точку, например «Волная 28». Потом откройте плитку объекта, добавьте кассиров в его состав и отметьте их по дням.' : 'Создавать объекты может тот, кому выдано право править график.'}
        />
      ) : (
        <>
          {vm.todayError !== null && <ErrorNotice error={vm.todayError} title="Сколько кассиров выходит сегодня — не получено" />}
          <ActionInfoQuiet>
            <ul className="sch-places" aria-label="Объекты графика" aria-busy={vm.refreshing || undefined}>
              {vm.places.map((place) => (
                <li className="sch-place" key={place.id}>
                  <Button variant="secondary" actionId="schedule.home.open-place" aria-label={`Открыть график: ${place.name}`} aria-describedby={vm.todayCounts !== null ? `sch-today-${place.id}` : undefined} onClick={() => actions.openPlace(place.id)}>
                    <CalendarDays size={22} aria-hidden="true" />
                    <span className="sch-place__name">{place.name}</span>
                    <span className="sch-place__hint">График выхода по дням</span>
                    {vm.todayCounts !== null && (
                      <span className="sch-place__today" id={`sch-today-${place.id}`}>
                        Сегодня выходят: {vm.todayCounts.get(place.id) ?? 0}
                      </span>
                    )}
                  </Button>
                  {vm.canManage && (
                    <span className="sch-place__edit">
                      <Button
                        variant="ghost"
                        icon={<Pencil size={14} aria-hidden="true" />}
                        actionId="schedule.home.edit-place"
                        aria-label={`Изменить объект: ${place.name}`}
                        title="Изменить объект"
                        onClick={() => actions.editPlace(place.id)}
                      />
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </ActionInfoQuiet>
        </>
      )}
    </section>
  )
}
